import { deleteNgFile, listNgFiles, readNgFile, renameNgFile, writeNgFile } from "./ngstore";
import { deleteNgThumbnail, readNgThumbnail, renameNgThumbnail } from "./ngthumbs";
import { writeOpfsFile } from "./opfswrite";

// Documents are stored in sumatrapdf-ng storage (see ngstore.ts), under their file names,
// and so are thumbnails (see ngthumbs.ts). What only our viewer needs is in OPFS, named
// after sha1 of the document: file state (<sha1>.state.js) and files extracted from a
// comic book archive.

export interface StoredDocumentFile {
  available: boolean;
  sha1: string;
  ext: string;
  fileName: string;
  stateFileName: string;
  originalFileName: string;
  size: number;
}

export interface ArchiveImageInfo {
  format: string;
  width: number;
  height: number;
  imageCount: number;
  orientation: number;
  browserRenderable: boolean;
}

export interface ArchiveFileState {
  Path: string;
  OriginalPath: string;
  Size: number;
  LastModified: number;
  IsImage: boolean;
  Image: ArchiveImageInfo | null;
}

export interface ExtractedArchiveFile {
  path: string;
  originalPath: string;
  data: Uint8Array;
  size: number;
  lastModified: number;
  image: ArchiveImageInfo | null;
}

export interface FileState {
  FilePath: string;
  OriginalFileName: string;
  Sha1: string;
  FileSize: number;
  PageCount: number;
  Favorites: unknown[];
  IsPinned: boolean;
  IsMissing: boolean;
  OpenCount: number;
  DecryptionKey: string | null;
  UseDefaultState: boolean;
  DisplayMode: string;
  ScrollPos: { X: number; Y: number };
  PageNo: number;
  Zoom: string;
  Rotation: number;
  WindowState: number;
  WindowPos: { X: number; Y: number; Dx: number; Dy: number };
  ShowToc: boolean;
  SidebarDx: number;
  DisplayR2L: boolean;
  BgCol: string;
  TabCol: string;
  ReparseIdx: number;
  TocState: number[] | null;
  ArchiveFiles?: ArchiveFileState[];
  LastOpenedAt: string;
  LastOpenedAtUnix: number;
  LastModifiedAt: string;
}

export interface HomeDocument {
  state: FileState;
  // false if the file was added in sumatrapdf-ng and not yet opened here,
  // in which case state is not saved and sha1 is empty
  hasState: boolean;
  sha1: string;
  // name of the file in sumatrapdf-ng storage, unique
  originalFileName: string;
  size: number;
  thumbnailURL: string | null;
  lastOpenedAtUnix: number;
}

export async function createStoredDocumentFile(data: Uint8Array, originalFileName: string): Promise<StoredDocumentFile> {
  const buffer = toArrayBuffer(data);
  const sha1 = await sha1Hex(buffer);
  const ext = extensionFromFileName(originalFileName) || "pdf";
  return {
    available: (await getOPFSRoot()) !== null,
    sha1,
    ext,
    fileName: `${sha1}.${ext}`,
    stateFileName: `${sha1}.state.js`,
    originalFileName,
    size: data.byteLength,
  };
}

export async function saveExtractedArchiveToOPFS(stored: StoredDocumentFile, files: ExtractedArchiveFile[]): Promise<boolean> {
  const root = await getOPFSRoot();
  if (!root) {
    return false;
  }
  if (await directoryExists(root, stored.fileName)) {
    return false;
  }
  try {
    const dir = await root.getDirectoryHandle(stored.fileName, { create: true });
    for (const file of files) {
      await writeNestedFile(dir, file.path, new Blob([toArrayBuffer(file.data)], { type: mimeTypeFromExt(stored.ext) }));
    }
    return true;
  } catch (e) {
    console.warn("failed to save extracted archive to OPFS", e);
    return false;
  }
}

export async function storedArchiveDirectoryExists(stored: StoredDocumentFile): Promise<boolean> {
  const root = await getOPFSRoot();
  return root ? directoryExists(root, stored.fileName) : false;
}

export async function loadFileStateFromOPFS(stored: StoredDocumentFile): Promise<FileState | null> {
  const root = await getOPFSRoot();
  if (!root) {
    return null;
  }
  try {
    const handle = await root.getFileHandle(stored.stateFileName);
    const text = await (await handle.getFile()).text();
    return parseStateFile(text);
  } catch {
    return null;
  }
}

export async function listHomeDocuments(): Promise<HomeDocument[]> {
  const root = await getOPFSRoot();
  const states = root ? await loadFileStatesByName(root) : new Map<string, FileState>();
  if (root) {
    await moveDocumentsFromOPFSToNg(root, states);
    await removeOldThumbnails(root);
  }

  const documents: HomeDocument[] = [];
  for (const file of await listNgFiles()) {
    const thumb = await readNgThumbnail(file.name, file.timestamp.getTime());
    const thumbnailURL = thumb ? URL.createObjectURL(thumb) : null;
    const state = states.get(file.name);
    if (!state) {
      const timestamp = Math.floor(file.timestamp.getTime() / 1000);
      documents.push({
        state: stateForFileWithoutState(file.name, timestamp),
        hasState: false,
        sha1: "",
        originalFileName: file.name,
        size: file.size,
        thumbnailURL,
        lastOpenedAtUnix: timestamp,
      });
      continue;
    }
    documents.push({
      state,
      hasState: true,
      sha1: state.Sha1,
      originalFileName: file.name,
      size: file.size,
      thumbnailURL,
      lastOpenedAtUnix: state.LastOpenedAtUnix,
    });
  }

  documents.sort(
    (a, b) =>
      Number(b.state.IsPinned) - Number(a.state.IsPinned) ||
      b.lastOpenedAtUnix - a.lastOpenedAtUnix ||
      a.originalFileName.localeCompare(b.originalFileName),
  );
  return documents;
}

// file name => state. If a file was replaced with a different one of the same name
// there is more than one state for the name, we pick the latest
async function loadFileStatesByName(root: FileSystemDirectoryHandle): Promise<Map<string, FileState>> {
  const states = new Map<string, FileState>();
  for await (const [name, handle] of root.entries()) {
    if (handle.kind !== "file" || !name.endsWith(".state.js")) {
      continue;
    }
    try {
      const stateText = await (await (handle as FileSystemFileHandle).getFile()).text();
      const state = parseStateFile(stateText);
      if (!state || state.IsMissing) {
        continue;
      }
      const prev = states.get(state.OriginalFileName);
      if (!prev || state.LastModifiedAt > prev.LastModifiedAt) {
        states.set(state.OriginalFileName, state);
      }
    } catch (e) {
      console.warn("failed to read OPFS file state", name, e);
    }
  }
  return states;
}

// we used to keep documents in OPFS as <sha1>.<ext>
async function moveDocumentsFromOPFSToNg(root: FileSystemDirectoryHandle, states: Map<string, FileState>): Promise<void> {
  let ngNames: Set<string> | null = null;
  for (const [name, state] of states) {
    let file: File;
    try {
      file = await (await root.getFileHandle(state.FilePath)).getFile();
    } catch {
      // the common case: already moved
      continue;
    }
    try {
      ngNames ??= new Set((await listNgFiles()).map((f) => f.name));
      if (!ngNames.has(name)) {
        await writeNgFile(name, new Uint8Array(await file.arrayBuffer()));
      }
      await root.removeEntry(state.FilePath);
    } catch (e) {
      console.warn("failed to move document from OPFS", name, e);
    }
  }
}

// thumbnails are now where sumatrapdf-ng keeps them; we used to keep them as
// <sha1>-thumb-<dx>-<dy>.png
async function removeOldThumbnails(root: FileSystemDirectoryHandle): Promise<void> {
  const old: string[] = [];
  for await (const [name, handle] of root.entries()) {
    if (handle.kind === "file" && /^[0-9a-f]{40}-thumb-\d+-\d+\.png$/.test(name)) {
      old.push(name);
    }
  }
  for (const name of old) {
    try {
      await root.removeEntry(name);
    } catch {
      // removed by another tab
    }
  }
}

function stateForFileWithoutState(fileName: string, timestamp: number): FileState {
  const stored: StoredDocumentFile = {
    available: false,
    sha1: "",
    ext: extensionFromFileName(fileName),
    fileName: "",
    stateFileName: "",
    originalFileName: fileName,
    size: 0,
  };
  return { ...createFileState(stored, 0), OpenCount: 0, LastOpenedAtUnix: timestamp };
}

export async function readHomeDocument(doc: HomeDocument): Promise<Uint8Array> {
  return readNgFile(doc.originalFileName);
}

// a file added in sumatrapdf-ng has no state until we need to remember something about it
async function storedAndStateForHomeDocument(doc: HomeDocument): Promise<{ stored: StoredDocumentFile; state: FileState }> {
  if (doc.hasState) {
    const stored = storedDocumentFromHomeDocument(doc);
    const state = (await loadFileStateFromOPFS(stored)) ?? doc.state;
    normalizeFileState(state);
    return { stored, state };
  }
  const data = await readNgFile(doc.originalFileName);
  const stored = await createStoredDocumentFile(data, doc.originalFileName);
  const state = (await loadFileStateFromOPFS(stored)) ?? { ...createFileState(stored, 0), OpenCount: 0 };
  return { stored, state };
}

export async function setHomeDocumentPinned(doc: HomeDocument, isPinned: boolean): Promise<void> {
  const { stored, state } = await storedAndStateForHomeDocument(doc);
  await saveFileStateToOPFS(stored, {
    ...state,
    OriginalFileName: doc.originalFileName,
    IsMissing: false,
    IsPinned: isPinned,
  });
}

export async function renameHomeDocument(doc: HomeDocument, originalFileName: string): Promise<void> {
  const trimmedName = originalFileName.trim();
  if (!trimmedName) {
    throw new Error("file name cannot be empty");
  }

  await renameNgFile(doc.originalFileName, trimmedName);
  await renameNgThumbnail(doc.originalFileName, trimmedName);
  if (!doc.hasState) {
    return;
  }
  const { stored, state } = await storedAndStateForHomeDocument(doc);
  await saveFileStateToOPFS(stored, {
    ...state,
    OriginalFileName: trimmedName,
  });
}

export async function removeHomeDocument(doc: HomeDocument): Promise<void> {
  await deleteNgFile(doc.originalFileName);
  await deleteNgThumbnail(doc.originalFileName);
  const root = await getOPFSRoot();
  if (!root || !doc.hasState) {
    return;
  }

  const { stored, state } = await storedAndStateForHomeDocument(doc);
  const stateFileName = stored.stateFileName;
  const namesToRemove: string[] = [];
  for await (const [name] of root.entries()) {
    if (name === stateFileName) {
      continue;
    }
    if (shouldRemoveHomeDocumentEntry(name, doc, state)) {
      namesToRemove.push(name);
    }
  }

  for (const name of namesToRemove) {
    try {
      await root.removeEntry(name, { recursive: true });
    } catch (e) {
      console.warn("failed to remove OPFS document file", name, e);
    }
  }

  await saveFileStateToOPFS(stored, {
    ...state,
    IsMissing: true,
  });
}

export function storedDocumentFromHomeDocument(doc: HomeDocument): StoredDocumentFile {
  const ext = extensionFromFileName(doc.state.FilePath) || "pdf";
  return {
    available: true,
    sha1: doc.sha1,
    ext,
    fileName: doc.state.FilePath,
    stateFileName: `${doc.sha1}.state.js`,
    originalFileName: doc.originalFileName,
    size: doc.state.FileSize,
  };
}

export async function readStoredArchiveFileFromOPFS(stored: StoredDocumentFile, path: string): Promise<File> {
  const root = await getOPFSRoot();
  if (!root) {
    throw new Error("OPFS is not available");
  }
  let dir = await root.getDirectoryHandle(stored.fileName);
  const parts = safePathParts(path);
  if (parts.length === 0) {
    throw new Error("empty archive path");
  }
  for (const part of parts.slice(0, -1)) {
    dir = await dir.getDirectoryHandle(part);
  }
  const handle = await dir.getFileHandle(parts[parts.length - 1]);
  return handle.getFile();
}

export function createFileState(
  stored: StoredDocumentFile,
  pageCount: number,
  existing?: FileState | null,
  archiveFiles?: ArchiveFileState[],
): FileState {
  const now = new Date().toISOString();
  const nowUnix = unixNow();
  return {
    FilePath: stored.fileName,
    OriginalFileName: stored.originalFileName,
    Sha1: stored.sha1,
    FileSize: stored.size,
    PageCount: pageCount,
    Favorites: existing?.Favorites ?? [],
    IsPinned: existing?.IsPinned ?? false,
    IsMissing: false,
    OpenCount: (existing?.OpenCount ?? 0) + 1,
    DecryptionKey: existing?.DecryptionKey ?? null,
    UseDefaultState: existing?.UseDefaultState ?? false,
    DisplayMode: existing?.DisplayMode ?? "single page",
    ScrollPos: existing?.ScrollPos ?? { X: 0, Y: 0 },
    PageNo: clamp(existing?.PageNo ?? 1, 1, Math.max(1, pageCount)),
    Zoom: existing?.Zoom ?? "fit page",
    Rotation: existing?.Rotation ?? 0,
    WindowState: existing?.WindowState ?? 3,
    WindowPos: existing?.WindowPos ?? { X: 0, Y: 0, Dx: 0, Dy: 0 },
    ShowToc: existing?.ShowToc ?? true,
    SidebarDx: existing?.SidebarDx ?? 0,
    DisplayR2L: existing?.DisplayR2L ?? false,
    BgCol: existing?.BgCol ?? "",
    TabCol: existing?.TabCol ?? "",
    ReparseIdx: existing?.ReparseIdx ?? 0,
    TocState: existing?.TocState ?? null,
    ArchiveFiles: archiveFiles ?? existing?.ArchiveFiles,
    LastOpenedAt: now,
    LastOpenedAtUnix: nowUnix,
    LastModifiedAt: now,
  };
}

export async function saveFileStateToOPFS(stored: StoredDocumentFile, state: FileState): Promise<void> {
  const root = await getOPFSRoot();
  if (!root) {
    return;
  }
  const nextState = { ...state, LastModifiedAt: new Date().toISOString() };
  try {
    await writeFile(root, stored.stateFileName, stateFileToJS(nextState));
  } catch (e) {
    console.warn("failed to save document state to OPFS", e);
  }
}

export function archiveFileStates(files: ExtractedArchiveFile[]): ArchiveFileState[] {
  return files.map((file) => ({
    Path: file.path,
    OriginalPath: file.originalPath,
    Size: file.size,
    LastModified: file.lastModified,
    IsImage: file.image !== null,
    Image: file.image,
  }));
}

export function isComicArchiveFileName(fileName: string): boolean {
  const ext = extensionFromFileName(fileName);
  return ext === "cbz" || ext === "cbr" || ext === "cb7";
}

function shouldRemoveHomeDocumentEntry(name: string, doc: HomeDocument, state: FileState): boolean {
  const shaFilePrefix = `${doc.sha1}.`;
  return name === state.FilePath || name.startsWith(shaFilePrefix);
}

async function getOPFSRoot(): Promise<FileSystemDirectoryHandle | null> {
  if (!navigator.storage?.getDirectory) {
    return null;
  }
  try {
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

async function fileExists(root: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await root.getFileHandle(name);
    return true;
  } catch {
    return false;
  }
}

async function directoryExists(root: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await root.getDirectoryHandle(name);
    return true;
  } catch {
    return false;
  }
}

async function writeFile(root: FileSystemDirectoryHandle, name: string, data: BlobPart): Promise<void> {
  const handle = await root.getFileHandle(name, { create: true });
  await writeOpfsFile(handle, data);
}

async function writeNestedFile(root: FileSystemDirectoryHandle, path: string, data: BlobPart): Promise<void> {
  const parts = safePathParts(path);
  if (parts.length === 0) {
    return;
  }
  let dir = root;
  for (const part of parts.slice(0, -1)) {
    dir = await dir.getDirectoryHandle(part, { create: true });
  }
  await writeFile(dir, parts[parts.length - 1], data);
}

function safePathParts(path: string): string[] {
  return path
    .replaceAll("\\", "/")
    .split("/")
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && part !== "." && part !== ".." && !part.includes(":"));
}

async function sha1Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-1", buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function toArrayBuffer(data: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy.buffer;
}

function extensionFromFileName(fileName: string): string {
  const match = /\.([A-Za-z0-9]+)$/.exec(fileName);
  return match ? match[1].toLowerCase() : "";
}

function mimeTypeFromExt(ext: string): string {
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "epub":
      return "application/epub+zip";
    case "mobi":
    case "azw":
    case "azw3":
    case "prc":
      return "application/x-mobipocket-ebook";
    case "djvu":
    case "djv":
      return "image/vnd.djvu";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
    case "jfif":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "bmp":
      return "image/bmp";
    case "webp":
      return "image/webp";
    case "avif":
      return "image/avif";
    case "ico":
      return "image/x-icon";
    case "svg":
      return "image/svg+xml";
    default:
      return "application/octet-stream";
  }
}

function stateFileToJS(state: FileState): string {
  return `export default ${JSON.stringify(state, null, 2)};\n`;
}

function parseStateFile(text: string): FileState | null {
  const trimmed = text.trim();
  const prefix = "export default ";
  if (!trimmed.startsWith(prefix)) {
    return null;
  }
  const json = trimmed.slice(prefix.length).replace(/;$/, "");
  const state = JSON.parse(json) as FileState;
  normalizeFileState(state);
  return state;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function normalizeFileState(state: FileState): void {
  state.OriginalFileName ||= state.FilePath;
  state.IsPinned = state.IsPinned === true;
  delete (state as { ThumbnailFileName?: string }).ThumbnailFileName;
  state.LastOpenedAtUnix ||= Math.floor(Date.parse(state.LastOpenedAt || state.LastModifiedAt || "") / 1000);
  if (!Number.isFinite(state.LastOpenedAtUnix) || state.LastOpenedAtUnix < 0) {
    state.LastOpenedAtUnix = 0;
  }
}

function unixNow(): number {
  return Math.floor(Date.now() / 1000);
}
